<script setup lang="ts">
import { useDebounceFn, useVModel } from '@vueuse/core'
import axios from 'axios'
import dayjs from 'dayjs'

import { extractSurveyIdsFromMessageContent } from '@/components/ui/TextareaWithVars/utils'
import { getCustomVars } from '../utils/campaignMessage'

import type { CustomVars } from '@/components/ui/TextareaWithVars/interfaces'
import type { CampaignType, LoyaltyReward, Survey } from '@/types/types'

const props = defineProps<{
  messageContent: string
  messageLength: number
  messageContentRef?: HTMLTextAreaElement
  selectedReward?: number | null
  selectedStoreIds: number[]
  rewards: LoyaltyReward[]
  surveys?: Survey[] | null
  surveysLoading?: boolean
  surveysError?: boolean
}>()

const emit = defineEmits<{
  (e: 'update:messageContent', value: string): void
  (e: 'update:messageLength', value: number): void
  (e: 'update:messageContentRef', value: HTMLTextAreaElement): void
  (e: 'onAddShortLink', text: string): void
  (e: 'hasMessageContentError', value: boolean): void
}>()

const route = useRoute()
const { t } = useI18n()
const { stores } = useLocalStorage()
const notification = useNotification()

const messageContentModel = useVModel(props, 'messageContent', emit)
const messageContentRefModel = useVModel(props, 'messageContentRef', emit)
const messageLength = useVModel(props, 'messageLength', emit)

const messageContentInput = ref<HTMLTextAreaElement | null>(null)

const isCheckingMessageLength = ref(false)
const variablesLength = ref<Record<string, number>>({})

const messageMaxLength = computed(() => (campaignType.value === 'sms' ? 145 : 150))
const campaignType = computed<CampaignType>(() => (route.query.type as CampaignType) || 'sms')

const longestStoreName = computed(() => {
  return stores.value
    .filter((store) => props.selectedStoreIds.includes(store.id))
    .reduce((longest, current) => (current.name.length > longest.name.length ? current : longest), {
      name: ''
    }).name
})

const customVariables = computed<CustomVars[]>(() => {
  return getCustomVars({
    t,
    variablesLength: variablesLength.value,
    storeName: longestStoreName.value,
    reward: props.rewards?.find((r) => r.id === props.selectedReward)
  })
})

const messageSurvey = computed(() => {
  const surveys = props.surveys ?? []
  if (!surveys.length) return null

  const surveyIds = extractSurveyIdsFromMessageContent(messageContentModel.value ?? '')
  const firstId = surveyIds[0]
  if (firstId == null || Number.isNaN(firstId)) return null

  return surveys.find((survey) => survey.id === firstId) ?? null
})

const handleMessageLengthCalculation = () => {
  isCheckingMessageLength.value = true
  messageLength.value = 0

  getMessageLength()
}

const getMessageLength = useDebounceFn(async () => {
  if (!messageContentModel.value?.length) {
    messageLength.value = 0
    isCheckingMessageLength.value = false
  } else {
    try {
      const { data } = await axios.get('campaigns/message-length', {
        params: {
          message: messageContentModel.value,
          storeIds: props.selectedStoreIds || [],
          rewardId: props.selectedReward,
          type: campaignType.value
        }
      })
      messageLength.value = data.data.messageLength

      isCheckingMessageLength.value = false
    } catch {
      messageLength.value = 0
      isCheckingMessageLength.value = false
      notification.create('error', {
        text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
      })
    }
  }
}, 500)

const getVariablesLength = async () => {
  try {
    const { data } = await axios.get('campaigns/variables-length', {
      params: {
        storeIds: props.selectedStoreIds || [],
        rewardId: props.selectedReward,
        type: campaignType.value
      }
    })
    variablesLength.value = data.data
  } catch {
    notification.create('error', {
      text: t('pages.campaign.campaignCreatePage.notifications.test.messageLengthCalculationError')
    })
  }
}

onMounted(() => {
  messageContentRefModel.value = messageContentInput.value
  getVariablesLength()
  handleMessageLengthCalculation()
})

defineExpose({
  handleMessageLengthCalculation
})
</script>

<template>
  <div class="rounded-md border bg-white">
    <div class="border-b px-4 py-5 sm:px-6">
      <h2 class="text-base font-medium text-foreground">
        {{ t('pages.campaign.campaignCreatePage.block.message.title') }}
      </h2>
    </div>

    <div class="px-4 py-5 sm:px-6">
      <div class="mt-1">
        <TextareaWithVars
          v-model="messageContentModel"
          v-model:message-content-ref="messageContentInput"
          label="messageContent"
          :max-length="messageMaxLength"
          :i18n-strings="{
            textareaLabel: t(`pages.campaign.campaignCreatePage.${campaignType}.form.messageContent`, {
              maxlength: messageMaxLength
            }),
            characters: t('global.words.characters'),
            insertVar: t('global.customVars.insertLabel'),
            errorEmojiInMessage: t('ui.error.strings.unauthorizedEmoji'),
            customValue: {
              placeholder: t('global.customVars.customValue.placeholder'),
              create: t('global.customVars.customValue.add'),
              update: t('global.customVars.customValue.modify'),
              back: t('global.customVars.customValue.back')
            }
          }"
          :custom-vars="customVariables"
          :sanitize-for-gsm="campaignType === 'sms'"
          :required-store-ids="selectedStoreIds"
          :surveys="surveys"
          :surveys-loading="surveysLoading"
          :surveys-error="surveysError"
          @has-error="emit('hasMessageContentError', $event)"
          @update:model-value="handleMessageLengthCalculation"
        >
          <template #messageLength>
            <span v-if="isCheckingMessageLength"> {{ t('global.words.loading') }}... </span>
            <span v-else>
              <span :class="{ 'text-red-600': messageLength > messageMaxLength }">
                {{ messageLength }}/{{ messageMaxLength }} {{ t('global.words.characters') }}
              </span>
            </span>
          </template>
          <BaseTip
            v-if="messageSurvey"
            severity="warn"
            class="my-5"
            :text="
              messageSurvey.endDate
                ? t('global.survey.linkedMessageTipWithEndDate', {
                    startDate: dayjs(messageSurvey.launchDate * 1000).format('DD/MM/YYYY'),
                    endDate: dayjs(messageSurvey.endDate * 1000).format('DD/MM/YYYY')
                  })
                : t('global.survey.linkedMessageTipFromStartDate', {
                    startDate: dayjs(messageSurvey.launchDate * 1000).format('DD/MM/YYYY')
                  })
            "
          />
        </TextareaWithVars>
      </div>
    </div>
  </div>
</template>
